import mustache from "mustache";
import { indiceGrpcCatalogService } from "../../../../modulos/catalogos/satelite/services/IndiceGrpcCatalogService";
import { IndiceItem } from "../../../../generated/grpc/catalogos_satelite_pb";
import { ClienteFincaBaseResponse } from "../../../../modulos/terceros/response/ClienteFincaBaseResponse";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { EscenaResponse } from "../../../../modulos/satelite/response/EscenaResponse";
import { formatDiaMesAbr, toDateOnly } from "../../../../utils/fechaUtils";
import { IConnectRender } from "../../../definitions/IConnectRender";
import { CapaOpcion, SateliteCapaMenuModal } from "./SateliteCapaMenuModal";
import { SateliteLoteSeccionRender } from "./SateliteLoteSeccionRender";
import { SateliteRangoFechasModal } from "./SateliteRangoFechasModal";
import template from "./sateliteBottomSheetTemplate.html";

mustache.parse(template);

/** Contador para ids únicos por instancia. */
let SHEET_ID = 0;

/** Bottom-sheet de Satélite, siempre visible (no depende de seleccionar un
 *  lote). Arma el select de capa (solo índices satelitales con tile en la
 *  escena activa — Ortomosaico volvió a TopActions), el rango de fechas, la
 *  leyenda del índice activo y la tira de escenas. Notifica hacia el mapa
 *  (`onCapaChange`) cada vez que cambia la capa o la escena elegida. */
export class SateliteBottomSheetRender implements IConnectRender {
  /** Id único de la instancia (para los ids del DOM). */
  private readonly _sheetId: number;

  /** Escenas mostrables: solo las que tienen cobertura calculada (ver el filtro
   *  del constructor). Todo lo que lee la lista pasa por acá. */
  private readonly _escenas: EscenaResponse[];

  /**
   * @param escenas Escenas satelitales de la finca, precargadas por el mapa
   *   (`SateliteMapRender.doInitData`) — la lista de escenas es data de
   *   finca, no del catálogo, así que no se auto-carga acá.
   * @param _onCapaChange Notifica `(capaId, escenaSeleccionada)` cada vez que
   *   cambia el select de capa o la escena activa, para que el mapa arme/quite
   *   la capa de tiles correspondiente.
   */
  constructor(
    escenas: EscenaResponse[],
    private readonly _onCapaChange?: (
      capaId: string,
      escena: EscenaResponse | null,
    ) => void,
  ) {
    SHEET_ID += 1;
    this._sheetId = SHEET_ID;
    // Las escenas sin cobertura calculada (nubes y sombras ambas nulas) no se
    // muestran: no hay forma de saber si la imagen sirve, y darlas por "claras"
    // las hacía elegibles como default automático. El filtro va acá y no en
    // `_renderEscenas` porque `render()` (`hasEscenas`) y `_fechasDisponibles()`
    // leen la misma lista: si se filtrara solo al pintar la tira, el rango de
    // fechas podría arrancar en un día sin botón visible, y una finca con todas
    // sus escenas sin dato mostraría una tira vacía en vez del mensaje de "sin
    // datos" que ya existe.
    this._escenas = escenas.filter(
      (escena) => this._porcentajeNubes(escena) != null,
    );
  }

  /** HTML del sheet: select de capa + rango de fechas + leyenda vacía si hay
   *  escenas; si no hay ninguna, mensaje de "sin datos" en su lugar. */
  render(): string {
    const disponibles = this._fechasDisponibles();
    return mustache.render(template, {
      id: this._sheetId,
      hasEscenas: disponibles !== null,
      desde: disponibles?.min ?? "",
      hasta: disponibles?.max ?? "",
      fechaMin: disponibles?.min ?? "",
      fechaMax: disponibles?.max ?? "",
    });
  }

  /** Cablea el select de capa, el rango de fechas y carga el catálogo de
   *  índices satelitales. */
  connect(): void {
    this._loteContainer = document.getElementById(`sbs-lote-${this._sheetId}`);
    this._capaSelect = document.getElementById(
      `sbs-capa-${this._sheetId}`,
    ) as HTMLSelectElement | null;
    this._legend = document.getElementById(`sbs-legend-${this._sheetId}`);
    this._legendLabel = document.getElementById(`sbs-legend-label-${this._sheetId}`);
    this._legendBar = document.getElementById(`sbs-legend-bar-${this._sheetId}`);
    this._desdeInput = document.getElementById(
      `sbs-desde-${this._sheetId}`,
    ) as HTMLInputElement | null;
    this._hastaInput = document.getElementById(
      `sbs-hasta-${this._sheetId}`,
    ) as HTMLInputElement | null;
    this._escenasContainer = document.getElementById(`sbs-escenas-${this._sheetId}`);
    this._fechasBtn = document.getElementById(
      `sbs-fechas-btn-${this._sheetId}`,
    ) as HTMLButtonElement | null;
    this._capaBtn = document.getElementById(
      `sbs-capa-btn-${this._sheetId}`,
    ) as HTMLButtonElement | null;

    this._capaSelect?.addEventListener("change", this._handleCapaSelectChange);
    this._desdeInput?.addEventListener("change", this._onRangoChange);
    this._hastaInput?.addEventListener("change", this._onRangoChange);
    this._fechasBtn?.addEventListener("click", this._onFechasBtnClick);
    this._capaBtn?.addEventListener("click", this._onCapaBtnClick);

    this._renderEscenas(true);

    indiceGrpcCatalogService.getListAsync().then((indices) => {
      this._indices = indices;
      this._renderCapaOptions();
    });
  }

  /** Libera los listeners del select de capa/rango de fechas y la sección de
   *  lote seleccionado si quedó montada. */
  dispose(): void {
    this._capaSelect?.removeEventListener("change", this._handleCapaSelectChange);
    this._desdeInput?.removeEventListener("change", this._onRangoChange);
    this._hastaInput?.removeEventListener("change", this._onRangoChange);
    this._fechasBtn?.removeEventListener("click", this._onFechasBtnClick);
    this._capaBtn?.removeEventListener("click", this._onCapaBtnClick);
    this._loteSeccion?.dispose();
  }

  /** Monta (o desmonta) la sección de lote seleccionado: título, datos y
   *  chart de estadísticas del índice activo. */
  setLoteSeleccionado(
    lote: ClienteFincaLoteResponse | null,
    finca: ClienteFincaBaseResponse | null,
  ): void {
    this._loteSeleccionado = lote;
    this._loteFinca = finca;
    this._renderLoteSeccion();
  }

  /** Índices satelitales del catálogo (disponibles tras `connect`). */
  private _indices: IndiceItem[] = [];
  /** Contenedor para la sección de lote seleccionado (vacío hasta entonces). */
  private _loteContainer: HTMLElement | null = null;
  private _loteSeleccionado: ClienteFincaLoteResponse | null = null;
  private _loteFinca: ClienteFincaBaseResponse | null = null;
  private _loteSeccion: SateliteLoteSeccionRender | null = null;
  private _capaSelect: HTMLSelectElement | null = null;
  private _legend: HTMLElement | null = null;
  private _legendLabel: HTMLElement | null = null;
  private _legendBar: HTMLElement | null = null;
  private _desdeInput: HTMLInputElement | null = null;
  private _hastaInput: HTMLInputElement | null = null;
  /** Botón "Fechas" (solo visible bajo el breakpoint de 999.8px del sheet). */
  private _fechasBtn: HTMLButtonElement | null = null;
  /** Botón "Capa" (solo visible bajo el breakpoint de 575.8px del sheet). */
  private _capaBtn: HTMLButtonElement | null = null;
  private _escenasContainer: HTMLElement | null = null;
  /** Escena elegida en la tira (la más reciente dentro del rango, por defecto). */
  private _escenaSeleccionada: EscenaResponse | null = null;
  /** `true` una vez aplicado el default inicial (NDVI) del select de capa. */
  private _defaultCapaAplicado = false;
  /** Opciones vigentes del select de capa (mismas que ve SateliteCapaMenuModal). */
  private _capaOpcionesActuales: CapaOpcion[] = [];
  /** Último (lote, índice, rango) con el que se armó `_loteSeccion` — evita
   *  recrearla si nada de esto cambió (ej. cambiar de escena sin que cambie
   *  el índice activo). */
  private _ultimaLoteSeccionKey: {
    loteId: string;
    indiceId: string | null;
    desde: string;
    hasta: string;
  } | null = null;

  /** Puebla el select solo con los índices que la escena activa realmente
   *  tiene renderizados (Ortomosaico ya no vive acá, volvió a TopActions). La
   *  primera vez que hay índices disponibles, propone NDVI por defecto; de
   *  ahí en más conserva la selección si sigue disponible, si no cae al
   *  primer índice disponible. */
  private _renderCapaOptions(): void {
    if (!this._capaSelect) return;

    const valorPrevio = this._capaSelect.value;
    const idsDisponibles = new Set(
      this._escenaSeleccionada?.indices.map((item) => item.indiceId) ?? [],
    );
    const indicesDisponibles = this._indices.filter((indice) =>
      idsDisponibles.has(indice.id),
    );

    this._capaOpcionesActuales = indicesDisponibles.map((indice) => ({
      value: indice.id,
      label: `${indice.nombre} - ${indice.descripcion}`,
    }));
    this._capaSelect.innerHTML = this._capaOpcionesActuales
      .map((opcion) => `<option value="${opcion.value}">${opcion.label}</option>`)
      .join("");

    if (!this._defaultCapaAplicado && indicesDisponibles.length > 0) {
      this._defaultCapaAplicado = true;
      const ndvi = indicesDisponibles.find((indice) => indice.nombre === "NDVI");
      this._capaSelect.value = ndvi?.id ?? indicesDisponibles[0]?.id ?? "";
    } else {
      const sigueDisponible = indicesDisponibles.some(
        (indice) => indice.id === valorPrevio,
      );
      this._capaSelect.value = sigueDisponible
        ? valorPrevio
        : (indicesDisponibles[0]?.id ?? "");
    }

    this._updateLegend();
    this._notifyCapaChange();
    this._renderLoteSeccion();
  }

  private _handleCapaSelectChange = (): void => {
    this._updateLegend();
    this._notifyCapaChange();
    this._renderLoteSeccion();
  };

  /** Notifica hacia el mapa la capa y la escena activas. */
  private _notifyCapaChange(): void {
    if (!this._capaSelect) return;
    this._onCapaChange?.(this._capaSelect.value, this._escenaSeleccionada);
  }

  /** Reconstruye la sección de lote seleccionado (título + datos + chart) si
   *  el lote, el índice activo o el rango de fechas cambiaron desde la última
   *  vez — no en cada llamada: esto se invoca también al cambiar de escena
   *  (para refrescar las opciones de capa), y la mayoría de las veces eso no
   *  cambia ni el índice ni el rango, así que no hace falta recrear el chart. */
  private _renderLoteSeccion(): void {
    if (!this._loteContainer) return;

    if (!this._loteSeleccionado || !this._loteFinca) {
      this._loteSeccion?.dispose();
      this._loteSeccion = null;
      this._ultimaLoteSeccionKey = null;
      this._loteContainer.innerHTML = "";
      return;
    }

    const indiceActivo = this._capaSelect
      ? (this._indices.find((i) => i.id === this._capaSelect!.value) ?? null)
      : null;
    const desde = this._desdeInput?.value ?? "";
    const hasta = this._hastaInput?.value ?? "";
    const key = {
      loteId: this._loteSeleccionado.id,
      indiceId: indiceActivo?.id ?? null,
      desde,
      hasta,
    };

    const previa = this._ultimaLoteSeccionKey;
    const sinCambios =
      previa !== null &&
      previa.loteId === key.loteId &&
      previa.indiceId === key.indiceId &&
      previa.desde === key.desde &&
      previa.hasta === key.hasta;
    if (sinCambios) return;

    this._ultimaLoteSeccionKey = key;
    this._loteSeccion?.dispose();
    this._loteSeccion = new SateliteLoteSeccionRender(
      this._loteSeleccionado,
      this._loteFinca,
      indiceActivo,
      desde,
      hasta,
    );
    this._loteContainer.innerHTML = this._loteSeccion.render();
    this._loteSeccion.connect();
  }

  /** Muestra la leyenda del índice activo (oculta si no hay ninguno seleccionable). */
  private _updateLegend(): void {
    if (!this._capaSelect || !this._legend) return;

    const indice = this._indices.find((i) => i.id === this._capaSelect!.value);
    if (!indice) {
      this._legend.style.display = "none";
      return;
    }

    this._legend.style.display = "";
    if (this._legendLabel) this._legendLabel.textContent = indice.labelColores;
    if (this._legendBar) {
      this._legendBar.style.background = `linear-gradient(90deg, ${indice.rangoColores.join(", ")})`;
    }
  }

  /** Rango de fechas disponible ("yyyy-mm-dd") según las escenas cargadas, o
   *  `null` si la finca no tiene ninguna. */
  private _fechasDisponibles(): { min: string; max: string } | null {
    if (this._escenas.length === 0) return null;

    const fechas = this._escenas.map((e) => toDateOnly(e.fecha)).sort();
    return { min: fechas[0], max: fechas[fechas.length - 1] };
  }

  private _onRangoChange = (): void => {
    this._renderEscenas(true);
  };

  /** Abre el modal de rango de fechas (pantallas angostas): al cambiar
   *  cualquiera de sus fechas, escribe el valor en los inputs ocultos de la
   *  fila principal y dispara el mismo flujo de refresco de siempre. */
  private _onFechasBtnClick = (): void => {
    if (!this._desdeInput || !this._hastaInput) return;

    new SateliteRangoFechasModal(
      this._desdeInput.value,
      this._hastaInput.value,
      this._desdeInput.min,
      this._desdeInput.max,
      (desde, hasta) => {
        this._desdeInput!.value = desde;
        this._hastaInput!.value = hasta;
        this._onRangoChange();
      },
    ).init();
  };

  /** Abre el modal de capa (pantallas angostas): al elegir una opción,
   *  escribe el value en el select oculto y dispara el mismo cambio de
   *  siempre (leyenda, notificación al mapa, sección de lote). */
  private _onCapaBtnClick = (): void => {
    if (!this._capaSelect) return;

    new SateliteCapaMenuModal(
      this._capaOpcionesActuales,
      this._capaSelect.value,
      (value) => {
        this._capaSelect!.value = value;
        this._handleCapaSelectChange();
      },
    ).init();
  };

  /** Pinta los botones de escena (más antigua a más reciente) filtrados por el
   *  rango desde/hasta activo; conserva la selección si sigue en rango, si no
   *  selecciona la más reciente del filtro. Repuebla el select de capa (los
   *  índices disponibles dependen de la escena) y notifica el cambio.
   *  @param centrarActiva Hace scroll a la escena activa (solo al iniciar o
   *  cambiar el rango de fechas; en un click manual se siente raro que el
   *  botón recién clickeado se mueva). */
  private _renderEscenas(centrarActiva: boolean): void {
    if (!this._escenasContainer) return;

    const desde = this._desdeInput?.value ?? "";
    const hasta = this._hastaInput?.value ?? "";
    const enRango = this._escenas.filter((escena) => {
      const fecha = toDateOnly(escena.fecha);
      return (!desde || fecha >= desde) && (!hasta || fecha <= hasta);
    });
    const filtradas = this._mejoresPorFecha(enRango).sort((a, b) =>
      a.fecha.localeCompare(b.fecha),
    );

    // Default automático (sin selección, o quedó fuera del rango filtrado):
    // la más reciente "clara". Una selección manual (aunque sea 100% nublada)
    // se respeta mientras siga en `filtradas` — no se pisa acá.
    if (!this._escenaSeleccionada || !filtradas.includes(this._escenaSeleccionada)) {
      this._escenaSeleccionada =
        [...filtradas].reverse().find((e) => this._esClara(e)) ?? null;
    }

    this._escenasContainer.innerHTML = filtradas
      .map((escena) => {
        const activa = escena === this._escenaSeleccionada;
        const clase = activa
          ? "sx-btn sx-btn-sm sx-btn-secondary"
          : this._esClara(escena)
            ? "sx-btn sx-btn-sm sx-btn-outline"
            : "sx-btn sx-btn-sm sx-btn-outline sbs-escena-nublada";
        const pct = this._porcentajeNubes(escena);
        const nubes = pct != null ? `${Math.round(pct)}%` : "—";
        return `<button type="button" class="${clase}" data-escena-id="${escena.id}">
          <span class="sbs-escena-nubes">${nubes}<i class="ph ph-cloud-fog"></i></span>
          <span class="sbs-escena-fecha">${this._fechaEscena(escena)}</span>
        </button>`;
      })
      .join("");

    this._escenasContainer
      .querySelectorAll<HTMLButtonElement>("button[data-escena-id]")
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const escena = filtradas.find((e) => e.id === btn.dataset.escenaId);
          if (!escena || escena === this._escenaSeleccionada) return;
          this._escenaSeleccionada = escena;
          this._renderEscenas(false);
        });
      });

    if (centrarActiva) {
      const btnActivo = this._escenaSeleccionada
        ? this._escenasContainer.querySelector<HTMLButtonElement>(
            `button[data-escena-id="${this._escenaSeleccionada.id}"]`,
          )
        : null;
      btnActivo?.scrollIntoView({ inline: "center", block: "nearest" });
    }

    this._renderCapaOptions();
  }

  /** "aaaa, DD Mes" (ej. "2026, 11 Jul"). */
  private _fechaEscena(escena: EscenaResponse): string {
    const anio = toDateOnly(escena.fecha).slice(0, 4);
    return `${anio}, ${formatDiaMesAbr(escena.fecha)}`;
  }

  /** Una escena con 90% de nubes o más sigue siendo seleccionable (estilo más
   *  opaco, ver `.sbs-escena-nublada`), pero no se propone como default
   *  automático. Compara sobre el valor redondeado (mismo criterio que el
   *  texto del botón, `Math.round`) para no tratar un "89.6% → se ve 90%"
   *  como claro. Sin dato nunca es clara — mismo criterio que
   *  `_puntajeCobertura`, aunque el filtro del constructor ya impide que llegue
   *  una escena así. */
  private _esClara(escena: EscenaResponse): boolean {
    const nubes = this._porcentajeNubes(escena);
    return nubes != null && Math.round(nubes) < 90;
  }

  /** Agrupa por fecha (puede haber más de una escena el mismo día) y deja solo
   *  la de menor cobertura (nubes + sombras) de cada grupo (null cuenta como
   *  el peor caso, para preferir siempre una escena con datos). */
  private _mejoresPorFecha(escenas: EscenaResponse[]): EscenaResponse[] {
    const porFecha = new Map<string, EscenaResponse>();
    for (const escena of escenas) {
      const fecha = toDateOnly(escena.fecha);
      const actual = porFecha.get(fecha);
      if (!actual || this._puntajeCobertura(escena) < this._puntajeCobertura(actual)) {
        porFecha.set(fecha, escena);
      }
    }
    return [...porFecha.values()];
  }

  /** `_porcentajeNubes`, o `Infinity` si no hay dato (para que rankee último). */
  private _puntajeCobertura(escena: EscenaResponse): number {
    return this._porcentajeNubes(escena) ?? Infinity;
  }

  /** Cobertura combinada nubes + sombras, tope 100% (ambas oscurecen la
   *  imagen por igual a fines prácticos); `null` si no hay ningún dato. */
  private _porcentajeNubes(escena: EscenaResponse): number | null {
    const { cloudsPercentage, shadowsPercentage } = escena;
    if (cloudsPercentage == null && shadowsPercentage == null) return null;
    return Math.min(100, (cloudsPercentage ?? 0) + (shadowsPercentage ?? 0));
  }
}
